import { z } from "zod";
import { DOCUMENT_KINDS, DocumentKindSchema, ItemCategory, ItemCategorySchema, PricingModeSchema, SupplyNature, SupplyNatureSchema, } from "./enums.js";
import { GstRateBpSchema, INT32_MAX, IsoDateSchema, PaiseSchema, optionalQuery, optionalText, } from "./primitives.js";
import { DEFAULT_UNIT_LABEL, UnitLabelSchema } from "./units.js";
/**
 * Request contracts for the HSN/SAC master and the items rate card
 * (spec §4 "items", §4.1 ad-hoc lines, §7 "Items & masters", §8.2, §8.3).
 *
 * REFRAME (spec §4, and it governs every default below): `items` is NOT a stock
 * ledger. It is a rate card — a library of jobs the business has already billed,
 * so a repeat job is two taps instead of retyping. Stock tracking is OFF by
 * default and may never be used. Everything here is shaped around fast picking:
 * the only genuinely required field on a new item is its name and its code.
 *
 * These schemas live in `@steelbill/types`, not in `apps/api`, so the SPA's
 * React Hook Form resolver and the server's `validate()` middleware enforce one
 * identical rule (spec §7).
 */
/* -------------------------------------------------------------------------- */
/* HSN / SAC code                                                              */
/* -------------------------------------------------------------------------- */
/**
 * `quotation_lines.hsn` / `invoice_lines.hsn` / `hsn_codes.code` are VarChar(8).
 *
 * Two different fields, deliberately named differently (spec §4):
 *  - `hsn_codes.code` is the MASTER row's code — the reference table, versioned
 *    by date (§8.7). Renamed from `hsn` in spec v2.
 *  - `quotation_lines.hsn` / `invoice_lines.hsn` is the SNAPSHOT taken onto the
 *    document at save time. It keeps the name `hsn` and must never be renamed:
 *    the line is a frozen historical record, not a pointer at the master.
 */
export const HSN_CODE_MAX_LENGTH = 8;
/** `hsn_codes.description` is VarChar(500). */
export const HSN_DESCRIPTION_MAX_LENGTH = 500;
/**
 * An HSN (goods) or SAC (service) code.
 *
 * Digits only, 4 to 8 of them: a legal HSN is 4, 6 or 8 digits and a SAC is 4
 * ("9954") or 6 ("995456") — both shapes appear in the seed (§8.2). The
 * pattern is deliberately stricter than the per-line `hsn` field on
 * `DocumentLineSchema`, because a code that reaches THIS schema is about to be
 * written into the master list that every future picker reads from; a typo there
 * propagates to every document that later picks it up.
 */
export const HsnCodeSchema = z
    .string()
    .trim()
    .regex(/^\d{4,8}$/, "HSN/SAC must be 4 to 8 digits, e.g. 7308 (goods) or 995456 (service)");
/** Every rate the business bills is 18% (§8.1, §8.2) — a starting default, always editable. */
export const DEFAULT_ITEM_GST_RATE_BP = 1800;
/* -------------------------------------------------------------------------- */
/* HSN master rows — versioned by date (§8.7)                                  */
/* -------------------------------------------------------------------------- */
/**
 * The start of GST. Rows seeded from the original notifications carry this as
 * their `effective_from`, matching the column default in `prisma/schema.prisma`.
 */
export const GST_EPOCH_DATE = "2017-07-01";
/**
 * §8.6 — the verification stamp is a pair. A date with no name is unattributable
 * ("verified on 12 Jan" — by whom?) and a name with no date never goes stale, so
 * the ADMIN screen can never show that the check is overdue. Either send both or
 * send neither.
 */
function refineVerification(value, ctx) {
    const hasDate = value.lastVerifiedOn !== undefined && value.lastVerifiedOn !== null;
    const hasName = value.lastVerifiedBy !== undefined && value.lastVerifiedBy !== null;
    if (hasDate === hasName)
        return;
    ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [hasDate ? "lastVerifiedBy" : "lastVerifiedOn"],
        message: hasDate
            ? "Record who verified this rate — a verification date with no name cannot be followed up"
            : "Record the date this rate was verified",
    });
}
/**
 * The same pairing rule on an update, where a third state exists: a key sent as
 * `null` CLEARS the stamp, and a key left out leaves it alone. Clearing only the
 * date would leave a verifier name attached to no date — the ADMIN screen could
 * then never show the check as overdue, which is the one thing §8.6 asks it to
 * show. So both halves must move together, in either direction.
 */
function refineVerificationUpdate(value, ctx) {
    const dateSent = value.lastVerifiedOn !== undefined;
    const nameSent = value.lastVerifiedBy !== undefined;
    if (!dateSent && !nameSent)
        return;
    if (dateSent !== nameSent) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [dateSent ? "lastVerifiedBy" : "lastVerifiedOn"],
            message: "Send the verification date and verifier together — set both or clear both (send null for each)",
        });
        return;
    }
    refineVerification(value, ctx);
}
const HsnDescriptionSchema = z
    .string()
    .trim()
    .min(1, "A description is required — it is what the picker shows")
    .max(HSN_DESCRIPTION_MAX_LENGTH, `Description must be ${HSN_DESCRIPTION_MAX_LENGTH} characters or fewer`);
const VerifiedBySchema = z
    .string()
    .trim()
    .min(1, "Verifier name cannot be empty")
    .max(191, "Verifier name must be 191 characters or fewer");
/**
 * Create one rate period for a code (ADMIN → Settings → Tax codes, §8.6).
 *
 * NOT keyed by code alone. Since spec v2 the master is versioned: uniqueness is
 * `(code, effective_from)`, so one code has ONE ROW PER RATE PERIOD and the
 * effective rate is resolved against the DOCUMENT date, never "now" (§8.7).
 *
 * Two fields are deliberately absent and are the SERVER's to set:
 *  - `supersededAt` — stamped on the PREVIOUS row by the rate-change procedure
 *    (see `RateChangeSchema`). A client that could set it directly could open a
 *    gap or an overlap in the timeline and make rate resolution ambiguous.
 *  - `isService` — denormalised from `supplyNature` (WORKS_CONTRACT is a service
 *    and prints as a SAC; the other two are goods and print as an HSN). Deriving
 *    it server-side is the only way the two can never disagree.
 */
export const HsnCodeCreateSchema = z
    .object({
    /**
     * The master column, `hsn_codes.code`. Named `code` since spec v2 — the
     * document lines' snapshot field is still `hsn` and is a different thing.
     */
    code: HsnCodeSchema,
    description: HsnDescriptionSchema,
    /** The rate EFFECTIVE FOR THIS PERIOD — not "the current rate for this code". */
    defaultGstRateBp: GstRateBpSchema.default(DEFAULT_ITEM_GST_RATE_BP),
    /**
     * §8.1 — default WORKS_CONTRACT, matching the column default: the two SACs
     * that cover nearly all of this business's billing are works-contract
     * services. (`items.supplyNature` defaults to FABRICATED_GOODS instead
     * because it answers a different question about a different row.)
     */
    supplyNature: SupplyNatureSchema.default(SupplyNature.WORKS_CONTRACT),
    /** Inclusive start of this rate period. Defaults to the start of GST. */
    effectiveFrom: IsoDateSchema.default(GST_EPOCH_DATE),
    /** §8.6 — set together, or not at all. */
    lastVerifiedOn: IsoDateSchema.optional(),
    lastVerifiedBy: VerifiedBySchema.optional(),
})
    .strict()
    .superRefine(refineVerification);
/**
 * Edit an existing rate period.
 *
 * `defaultGstRateBp` and `effectiveFrom` are NOT editable here, and that is the
 * whole point of §8.7: *"Never edit a rate in place, and never back-fill
 * existing documents."* Editing the rate on a live row would silently change
 * what every future document resolves for a period that has already been billed
 * against. A rate change is a NEW ROW — `RateChangeSchema` below.
 *
 * What remains editable is presentation and provenance: the description shown in
 * the picker, the goods/service classification, and the §8.6 verification stamp.
 */
export const HsnCodeUpdateSchema = z
    .object({
    description: HsnDescriptionSchema.optional(),
    supplyNature: SupplyNatureSchema.optional(),
    lastVerifiedOn: IsoDateSchema.nullish(),
    lastVerifiedBy: VerifiedBySchema.nullish(),
})
    .strict()
    .superRefine((value, ctx) => {
    if (Object.keys(value).length === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [],
            message: "Send at least one field to update",
        });
        return;
    }
    refineVerificationUpdate(value, ctx);
});
/**
 * §8.7 — the whole rate-change procedure, as one request.
 *
 *   1. ADMIN opens Settings → Tax codes.
 *   2. Adds a new row for the code with the new rate and its `effective_from`.
 *   3. The app stamps `superseded_at` on the previous row with that same date.
 *   4. Done. No deploy, no migration, no code edit.
 *
 * Steps 2 and 3 must be one transaction, so they are one request body: a client
 * that could do them separately could leave two rows both claiming to be current
 * (rate resolution then returns whichever the index reaches first) or a gap
 * where a document date matches no row at all.
 *
 * `effectiveFrom` has NO DEFAULT here — unlike on create, where the start of GST
 * is the honest answer. A rate change without the date the notification takes
 * effect from is not a rate change; back-dating and post-dating are both normal
 * and must be stated, not assumed to be today.
 */
export const RateChangeSchema = z
    .object({
    /** The master code whose rate is changing (`hsn_codes.code`). */
    code: HsnCodeSchema,
    /** The new rate, applying from `effectiveFrom` onwards. */
    newGstRateBp: GstRateBpSchema,
    /**
     * The date the new rate takes effect: inclusive start of the new row, and
     * the exclusive `superseded_at` stamped on the row it replaces. One date for
     * both ends is what makes the timeline contiguous with no overlap.
     */
    effectiveFrom: IsoDateSchema,
    /** Carried forward from the superseded row when absent. */
    description: HsnDescriptionSchema.optional(),
    supplyNature: SupplyNatureSchema.optional(),
    /**
     * §8.6 — a rate change is exactly the moment the CA confirmed something, so
     * this is where the verification stamp is most worth capturing.
     */
    lastVerifiedOn: IsoDateSchema.optional(),
    lastVerifiedBy: VerifiedBySchema.optional(),
})
    .strict()
    .superRefine(refineVerification);
/* -------------------------------------------------------------------------- */
/* Item create / update                                                        */
/* -------------------------------------------------------------------------- */
export const ItemCreateBaseSchema = z.object({
    /** The job title as it will be printed, e.g. "Repairing for MS Rolling shutter". */
    name: z
        .string()
        .trim()
        .min(1, "Item name is required")
        .max(191, "Item name must be 191 characters or fewer"),
    /** Longer printable text when the job title alone is not enough (§3). */
    description: optionalText(1000),
    /** §4 — default SERVICE, because nearly every line he bills is a service job. */
    category: ItemCategorySchema.default(ItemCategory.SERVICE),
    /**
     * §8.1 — "default new items to FABRICATED_GOODS / HSN 7308". Note this is a
     * different default from `category`: the two answer different questions.
     * `category` is how the picker is grouped; `supplyNature` is the GST
     * classification that decides whether the code prints as an HSN or a SAC.
     */
    supplyNature: SupplyNatureSchema.default(SupplyNature.FABRICATED_GOODS),
    /**
     * The code itself, not a foreign key. The client picks from `GET /hsn`, which
     * returns codes; making the API speak codes keeps the SPA from having to hold
     * master-table ids, and lets the server resolve (or create) the master row.
     *
     * Still named `hsn`, deliberately. This is the item request's code field, the
     * same name the document lines use, not the master column — the server
     * resolves it against `hsn_codes.code` for the relevant date (§8.7) and stores
     * `items.hsn_id`. Only the MASTER column was renamed `hsn` → `code` in v2.
     */
    hsn: HsnCodeSchema,
    gstRateBp: GstRateBpSchema.default(DEFAULT_ITEM_GST_RATE_BP),
    /**
     * §3 — default LUMPSUM; it is what every real invoice line uses. The literal
     * rather than a const-object member: `PricingMode` is owned by
     * `@steelbill/tax` and re-exported as a TYPE only, so there is no runtime
     * object to read it from here.
     */
    pricingMode: PricingModeSchema.default("LUMPSUM"),
    /** §4 — default `NO`. */
    unitLabel: UnitLabelSchema.default(DEFAULT_UNIT_LABEL),
    /** Per-unit rate in paise. A remembered default, re-editable on every document. */
    ratePaise: PaiseSchema.default(0),
    /** §4 — stock tracking is OFF by default and may never be switched on. */
    trackStock: z.boolean().default(false),
    /** Opening stock. Only meaningful when `trackStock` is true — see the refinement. */
    stockQty: z
        .number()
        .int("Stock quantity must be a whole number")
        .nonnegative("Stock quantity cannot be negative")
        .max(INT32_MAX, "Stock quantity is too large")
        .nullish(),
});
/**
 * Rejecting `stockQty` on an untracked item is not pedantry: spec §4 says stock
 * is off by default and §7 says stock adjustment is "only meaningful when
 * track_stock is on". Accepting an opening quantity that nothing will ever
 * maintain would quietly create a number the owner believes in.
 */
function refineStock(value, ctx) {
    if (value.trackStock === false && value.stockQty !== undefined && value.stockQty !== null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["stockQty"],
            message: "Stock quantity only applies when stock tracking is switched on for this item",
        });
    }
}
/**
 * `.strict()` throughout: an unrecognised key is a 400, never a silent drop. On
 * an update in particular, quietly ignoring a misspelled `ratePaisa` would look
 * to the user exactly like a save that worked and didn't.
 */
export const ItemCreateSchema = ItemCreateBaseSchema.strict().superRefine(refineStock);
/**
 * Every field optional — but `.partial()` also strips the defaults, which is the
 * point: an update must never reset `pricingMode` to LUMPSUM just because the
 * form didn't send it.
 *
 * `stockQty` IS DELIBERATELY ABSENT, and `.strict()` therefore 400s it.
 * ------------------------------------------------------------------
 * Changing an existing item's stock is `item.adjust_stock`, which §2.1 puts on
 * the ADMIN-only row and §6 marks "(ADMIN)". This endpoint is gated on
 * `item.edit`, which the OWNER holds — leaving `stockQty` writable here let the
 * OWNER set any quantity through PUT after being refused by
 * `POST /items/:id/adjust-stock`, which is the same write with the capability
 * check removed. So the field is not merely gated here, it is not accepted here
 * at all: stock moves only through adjust-stock, which is also the only place
 * the signed delta and its audited reason (§13) exist.
 *
 * `stockQty` STAYS on {@link ItemCreateSchema}: an opening balance on a
 * brand-new item has no history to corrupt and no adjustment it could
 * contradict. Switching `trackStock` ON through this endpoint therefore starts
 * the item at 0 and leaves adjust-stock to establish the real figure — see
 * `apps/api/src/routes/items.ts`.
 *
 * Items are NEVER hard-deleted (spec §2 for invoices, and the same rule applies
 * here because issued documents point back at the item). Deactivating is
 * `{ "isActive": false }` on this endpoint.
 */
export const ItemUpdateSchema = ItemCreateBaseSchema.omit({ stockQty: true })
    .partial()
    .extend({ isActive: z.boolean().optional() })
    .strict()
    .superRefine((value, ctx) => {
    if (Object.keys(value).length === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [],
            message: "Send at least one field to update",
        });
    }
});
/* -------------------------------------------------------------------------- */
/* Query strings                                                               */
/* -------------------------------------------------------------------------- */
/**
 * Query strings are always strings, so a boolean flag arrives as "true"/"1".
 * Module-local on purpose — every list endpoint declares its own query schema.
 */
const BooleanFlag = z
    .union([z.boolean(), z.enum(["true", "false", "1", "0"])])
    .transform((value) => value === true || value === "true" || value === "1");
const SearchTerm = z
    .string()
    .trim()
    .min(1, "Search term cannot be empty")
    .max(191, "Search term is too long");
/** Default page size for the picker: enough to scroll, small enough to be instant. */
export const ITEM_LIST_DEFAULT_LIMIT = 50;
export const ITEM_LIST_MAX_LIMIT = 200;
/**
 * Every field is wrapped in `optionalQuery` because spec §7 documents this
 * endpoint as `GET /items?q=&category=` — the SPA sends every key whether or not
 * the user filled it in. Without the wrapper an empty search box 400s the item
 * picker, which is the OWNER's primary daily screen (§11), and `?limit=` coerces
 * to `Number("") === 0` so the default never fires.
 */
export const ItemListQuerySchema = z
    .object({
    /** Matches item name, description or its HSN/SAC code. */
    q: optionalQuery(SearchTerm.optional()),
    category: optionalQuery(ItemCategorySchema.optional()),
    /** Defaults to true — a deactivated job should never surface in the picker. */
    active: optionalQuery(BooleanFlag.default(true)),
    limit: optionalQuery(z.coerce
        .number()
        .int("limit must be a whole number")
        .min(1, "limit must be at least 1")
        .max(ITEM_LIST_MAX_LIMIT, `limit cannot exceed ${ITEM_LIST_MAX_LIMIT}`)
        .default(ITEM_LIST_DEFAULT_LIMIT)),
    offset: optionalQuery(z.coerce
        .number()
        .int("offset must be a whole number")
        .min(0, "offset cannot be negative")
        .default(0)),
})
    .strict();
/** The HSN master is small (§8.2 = a dozen rows), so the page size is too. */
export const HSN_SEARCH_DEFAULT_LIMIT = 20;
export const HSN_SEARCH_MAX_LIMIT = 100;
/** `optionalQuery` throughout — spec §7 documents this as `GET /hsn?q=`. */
export const HsnSearchQuerySchema = z
    .object({
    /** Matches the code by prefix or the description by substring. Omit to list all. */
    q: optionalQuery(SearchTerm.optional()),
    /**
     * §8.7 — resolve rates AS OF a document date, not "now". The picker on a
     * back-dated invoice must offer the rate that was effective on the invoice
     * date, otherwise it silently proposes today's rate for a period that was
     * billed under a different one. Absent means today (server-side, in
     * Asia/Kolkata — never the client's clock).
     */
    asOf: optionalQuery(IsoDateSchema.optional()),
    limit: optionalQuery(z.coerce
        .number()
        .int("limit must be a whole number")
        .min(1, "limit must be at least 1")
        .max(HSN_SEARCH_MAX_LIMIT, `limit cannot exceed ${HSN_SEARCH_MAX_LIMIT}`)
        .default(HSN_SEARCH_DEFAULT_LIMIT)),
})
    .strict();
/** How many codes `GET /hsn/frequent` returns — it feeds a row of chips (§4.1). */
export const HSN_FREQUENT_DEFAULT_LIMIT = 8;
export const HSN_FREQUENT_MAX_LIMIT = 25;
export const HsnFrequentQuerySchema = z
    .object({
    limit: optionalQuery(z.coerce
        .number()
        .int("limit must be a whole number")
        .min(1, "limit must be at least 1")
        .max(HSN_FREQUENT_MAX_LIMIT, `limit cannot exceed ${HSN_FREQUENT_MAX_LIMIT}`)
        .default(HSN_FREQUENT_DEFAULT_LIMIT)),
})
    .strict();
/** `:id` path parameter. Params arrive as strings, so it is coerced. */
export const ItemIdParamSchema = z
    .object({
    id: z.coerce
        .number()
        .int("Item id must be a whole number")
        .positive("Item id must be positive"),
})
    .strict();
/* -------------------------------------------------------------------------- */
/* "Save to my list" — promote an ad-hoc document line (§4.1)                   */
/* -------------------------------------------------------------------------- */
/**
 * Which document's line is being promoted — one value per document register.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * IT IS `DOCUMENT_KINDS`, DERIVED, NOT A HAND-KEPT PAIR. This read
 * `["quotation", "invoice"]` until 2026-08-16, so `POST /items/from-line` 400'd
 * a challan line — while `DeliveryChallanLine` carries both `item_id` AND
 * `saved_as_item_id` (prisma/schema.prisma) and `Item.challanLines` is a real
 * relation. The columns existed and no code path could ever write them.
 *
 * That matters because §3.1 requires ZERO upfront data entry: the rate card
 * grows itself out of lines already billed. Job-work movements are exactly the
 * repeat work a fabricator does monthly, so refusing them meant the library
 * stopped learning from a whole class of document.
 *
 * A test asserted `lineType: "challan"` was a 400 — encoding the gap as
 * intended behaviour. It was rewritten, not adjusted.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export const LINE_TYPES = DOCUMENT_KINDS;
export const LineTypeSchema = DocumentKindSchema;
/**
 * The prompt shown after saving a document with ad-hoc lines (§4.1): *"Save
 * these to your item list for next time?"*. One call per accepted checkbox.
 *
 * `lineType` is required because each register's lines are a separate table with
 * an independent id sequence — `lineId` alone is ambiguous and would eventually
 * promote the wrong job.
 */
export const ItemFromLineSchema = z
    .object({
    lineId: z.coerce
        .number()
        .int("lineId must be a whole number")
        .positive("lineId must be positive"),
    lineType: LineTypeSchema,
    /** Optional overrides — the line's own snapshot is used when they are absent. */
    name: z.string().trim().min(1).max(191).optional(),
    category: ItemCategorySchema.optional(),
})
    .strict();
/* -------------------------------------------------------------------------- */
/* Stock adjustment (ADMIN, §7)                                                */
/* -------------------------------------------------------------------------- */
/**
 * A signed delta rather than a new absolute quantity: "+12 received" / "-3
 * damaged" is what a human actually knows, and it is what makes the audit row
 * (§13) tell a story instead of just a pair of numbers.
 */
export const AdjustStockSchema = z
    .object({
    // Bounded in BOTH directions: the route computes `current + delta` in JS and
    // writes the result to a 32-bit column, so an unbounded delta overflows it.
    delta: z
        .number()
        .int("Stock adjustment must be a whole number")
        .min(-INT32_MAX, "Stock adjustment is too large")
        .max(INT32_MAX, "Stock adjustment is too large")
        .refine((value) => value !== 0, "Adjustment cannot be zero"),
    reason: z
        .string()
        .trim()
        .min(1, "A reason is required for every stock adjustment")
        .max(500, "Reason must be 500 characters or fewer"),
})
    .strict();
//# sourceMappingURL=items.js.map